import type { Station } from './types';

/** Synoptic networks (MNET_ID) worth showing first: airport stations, then fire-weather stations. */
export const PREFERRED_NETWORKS = ['1', '2'];

const HOUR = 3600_000;

/** How much a station is worth showing: trusted networks, complete wind data, recent observations. */
export function stationScore(s: Station, now: number): number {
  const network = PREFERRED_NETWORKS.indexOf(s.network);
  return (
    (network < 0 ? 0 : 2 * (PREFERRED_NETWORKS.length - network)) +
    (s.windGust !== null ? 1 : 0) +
    (s.windDirection !== null ? 1 : 0) -
    Math.min(2, (now - s.time) / HOUR)
  );
}

/** Which stations take part, beyond their score. */
export interface ThinOptions {
  /** Kept whatever their spacing (still on screen), e.g. those already shown. */
  pinned?: ReadonlySet<string>;
  /** Win against similar stations, so a recomputed spread changes little. */
  preferred?: ReadonlySet<string>;
}

/** Score bonus of preferred stations: worth about one notch of data quality. */
const PREFERRED_BONUS = 1.5;

/**
 * Picks stations, best first, so that no two picked ones are closer than `spacing` on screen:
 * the map stays evenly filled at any zoom. `project` gives screen pixels, or null when off-screen.
 */
export function thinStations(
  stations: readonly Station[],
  spacing: number,
  project: (s: Station) => { x: number; y: number } | null,
  now: number,
  { pinned, preferred }: ThinOptions = {},
): Station[] {
  const ranked = stations
    .map((s) => ({
      s,
      pin: pinned?.has(s.id) ?? false,
      score: stationScore(s, now) + (preferred?.has(s.id) ? PREFERRED_BONUS : 0),
      p: project(s),
    }))
    .filter((r) => r.p !== null)
    // Ties broken by id, so the same stations win every time and the map does not flicker.
    .sort(
      (a, b) =>
        Number(b.pin) - Number(a.pin) ||
        b.score - a.score ||
        (a.s.id < b.s.id ? -1 : a.s.id > b.s.id ? 1 : 0),
    );

  const grid = new Map<string, { x: number; y: number }[]>();
  const picked: Station[] = [];
  const spacing2 = spacing * spacing;
  for (const { s, p, pin } of ranked) {
    const { x, y } = p!;
    const gx = Math.floor(x / spacing);
    const gy = Math.floor(y / spacing);
    let free = true;
    for (let dx = -1; dx <= 1 && free && !pin; dx++)
      for (let dy = -1; dy <= 1 && free; dy++)
        for (const q of grid.get(`${gx + dx},${gy + dy}`) ?? [])
          if ((q.x - x) ** 2 + (q.y - y) ** 2 < spacing2) free = false;
    if (!free) continue;
    const key = `${gx},${gy}`;
    grid.set(key, [...(grid.get(key) ?? []), { x, y }]);
    picked.push(s);
  }
  return picked;
}

/** The stations on the map and the zoom their spread was computed for. */
export interface Selection {
  zoom: number;
  ids: ReadonlySet<string>;
  stations: Station[];
}

/**
 * How much the zoom may change before the spread is recomputed: small zoom changes keep the stations
 * that are shown. Less from zoom 10 on, so zooming into an area soon reveals the stations in it.
 */
export const respreadZoom = (zoom: number) => (zoom >= 10 ? 0.5 : 1);

export interface SelectionQuery {
  stations: readonly Station[];
  previous: Selection | null;
  zoom: number;
  /** Wanted spacing, px, and the closest stations may come while zooming out, px. */
  spacing: number;
  minSpacing: number;
  project: (s: Station) => { x: number; y: number } | null;
  now: number;
}

/**
 * The stations to show, changing as little as possible: within `respreadZoom` the shown ones stay and
 * only gaps are filled (spacing measured at the zoom of the spread); beyond it the spread is recomputed,
 * keeping every shown station when zooming in and preferring them when zooming out.
 */
export function selectStations({
  stations,
  previous,
  zoom,
  spacing,
  minSpacing,
  project,
  now,
}: SelectionQuery): Selection {
  const pick = (base: number, options: ThinOptions) => {
    const picked = thinStations(stations, spacing * 2 ** (zoom - base), project, now, options);
    return { zoom: base, ids: new Set(picked.map((s) => s.id)), stations: picked };
  };
  if (!previous) return pick(zoom, {});
  const scale = 2 ** (zoom - previous.zoom);
  const keep =
    Math.abs(zoom - previous.zoom) < respreadZoom(Math.max(zoom, previous.zoom)) &&
    spacing * scale >= minSpacing;
  if (keep) return pick(previous.zoom, { pinned: previous.ids });
  return zoom > previous.zoom
    ? pick(zoom, { pinned: previous.ids })
    : pick(zoom, { preferred: previous.ids });
}
